import styles from './statistics-styles.module.scss';

import { Box } from '@mui/material';
import { ColumnDef, useReactTable } from '@tanstack/react-table';
import { getSortedRowModel, Row } from '@tanstack/table-core';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TimeCard } from '@work4all/models/lib/Classes/TimeCard.entity';
import { UserPresenceTimeInfo } from '@work4all/models/lib/Classes/UserPresenceTimeInfo.entity';
import { WorkingTimeTimeCardDayInfo } from '@work4all/models/lib/Classes/WorkingTimeTimeCardDayInfo.entity';

import { DateTimeCustom } from '@work4all/utils/lib/date-utils/formatDateString';

import { ICssClasses } from '../types';
import { useDefaultReactTableOptions } from '../utils/use-default-react-table-options';
import { VirtualizedTableV8 } from '../VirtualizedTableV8';

import { useStatisticTimeCardTableRowSelection } from './useRowSelection';

interface StatisticTimeCardTableProps {
  data: TimeCard;
  onSelectedRowsChange?: (
    selectedRows: Row<WorkingTimeTimeCardDayInfo>[]
  ) => void;
  maxTargetTimeMinutes: number;
  tableClasses?: ICssClasses;
}

export function renderTime(totalMinutes: number | string) {
  totalMinutes = parseFloat(totalMinutes + '');
  if (!totalMinutes) {
    return <span>&nbsp;</span>;
  }
  totalMinutes = Math.round(totalMinutes);
  const minutes = Math.abs(totalMinutes % 60).toFixed(0);
  const hours = Math.floor(Math.abs(totalMinutes) / 60).toFixed(0);

  return (
    <span className={clsx({ [styles.red]: totalMinutes < 0 })}>
      {totalMinutes < 0 && '-'}
      {`${padTo2Digits(hours)}:${padTo2Digits(minutes)}`}
    </span>
  );
}

export function padTo2Digits(num) {
  return num.toString().padStart(2, '0');
}

function isMinDate(date: string) {
  return date === '0001-01-01T00:00:00';
}

export const StatisticTimeCardTable: React.FC<StatisticTimeCardTableProps> = (
  props
) => {
  const { data, maxTargetTimeMinutes, tableClasses } = props;
  const { t } = useTranslation();

  const totalValues = useMemo(() => {
    return {
      pauseAmountMinutes: data.timeCardDays
        ?.map((x) => x.pauseAmountMinutes)
        .reduce((a, b) => a + b, 0),
      targetMinutes: data.timeCardDays
        ?.map((x) => x.targetMinutes)
        .reduce((a, b) => a + b, 0),
      isGrossMinutes: data.timeCardDays
        ?.map((x) => x.isGrossMinutes)
        .reduce((a, b) => a + b, 0),
      differenceminutes: data.timeCardDays
        ?.map((x) => x.differenceminutes)
        .reduce((a, b) => a + b, 0),
      timeAccountMinutes: data.timeCardDays
        ?.map((x) => x.timeAccountMinutes)
        .reduce((a, b) => a + b, 0),
      payedTime: data.timeCardDays
        ?.map((x) => x.istNetMinutes)
        .reduce((a, b) => a + b, 0),
      projectTimeTotal: data.timeCardDays
        ?.map((x) => x.projectTimeTotal * 60)
        .reduce((a, b) => a + b, 0),
      projectTimeDifference: data.timeCardDays
        ?.map(
          (x) =>
            x.projectTimeTotal * 60 -
            x.isGrossMinutes +
            x.amountSickness +
            x.pauseAmountMinutes
        )
        .reduce((a, b) => a + b, 0),
    };
  }, [data.timeCardDays]);

  const columns: ColumnDef<WorkingTimeTimeCardDayInfo, any>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('COMMON.DATE'),
        accessorKey: 'date',
        cell: (props) => {
          const value = props.getValue();

          return (
            <div className={styles.container}>
              <div className={styles.beginCell}>
                {DateTime.fromJSDate(new Date(value)).weekdayShort + '. '}
              </div>
              <div className={styles.endCell}>
                {DateTime.fromJSDate(new Date(value)).toLocaleString(
                  DateTimeCustom.DATE_DAY_MONTH
                )}
              </div>
            </div>
          );
        },
        size: 90,
      },
      {
        id: 'startDate',
        header: `${t('COMMON.BEGINNING')} / ${t('COMMON.END')}`,
        accessorKey: 'startDate',
        size: 110,
        cell: (props) => {
          if (props.row.original.amountSickness > 0) {
            return (
              props.row.original.sicknessList?.[0]?.sicknessType?.name ||
              t('COMMON.SICKNESSDAY')
            );
          } else if (props.row.original.amountHoliday > 0) {
            return t('COMMON.HOLIDAY');
          } else if (props.row.original.amountVacation > 0) {
            return (
              props.row.original.vacationList?.[0]?.vacationKind?.name ||
              t('COMMON.VACATIONDAY')
            );
          } else {
            if (props.row.original.presenceList?.[0]?.start === undefined) {
              return '...';
            } else {
              return props.row.original.presenceList.map(
                (item: UserPresenceTimeInfo, i: number) => {
                  return (
                    <Fragment key={`stard-end-cell-${i}`}>
                      {!isMinDate(item.start) && (
                        <div className={styles.beginCell}>
                          {DateTime.fromISO(item.start).toLocaleString(
                            DateTime.TIME_SIMPLE
                          )}
                        </div>
                      )}
                      {!isMinDate(item.end) && (
                        <div className={styles.endCell}>
                          {DateTime.fromISO(item.end).toLocaleString(
                            DateTime.TIME_SIMPLE
                          )}
                        </div>
                      )}
                    </Fragment>
                  );
                }
              );
            }
          }
        },
        alignment: 'center',
      },
      {
        id: 'pauseAmountMinutes',
        header: t('COMMON.PAUSE'),
        accessorKey: 'pauseAmountMinutes',
        footer: () => (
          <Box height="100%">{renderTime(totalValues.pauseAmountMinutes)}</Box>
        ),
        size: 70,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'targetMinutes',
        header: t('COMMON.SHOULD'),
        accessorKey: 'targetMinutes',
        footer: () => (
          <Box textAlign="center">
            {renderTime(totalValues.targetMinutes)}
            {maxTargetTimeMinutes !== 0 && (
              <span>
                <br />({renderTime(maxTargetTimeMinutes)})
              </span>
            )}
          </Box>
        ),
        size: 60,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'isGrossMinutes',
        header: t('COMMON.IS'),
        accessorKey: 'isGrossMinutes',
        footer: () => (
          <Box height="100%">{renderTime(totalValues.isGrossMinutes)}</Box>
        ),
        size: 60,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'paid',
        header: t('COMMON.PAYED'),
        footer: () => (
          <Box height="100%">{renderTime(totalValues.payedTime)}</Box>
        ),
        size: 80,
        cell: (data: { row: { original: WorkingTimeTimeCardDayInfo } }) => {
          const value = data.row.original.istNetMinutes;
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'differenceminutes',
        header: t('COMMON.DAY'),
        accessorKey: 'differenceminutes',
        footer: () => (
          <Box height="100%">{renderTime(totalValues.differenceminutes)}</Box>
        ),
        size: 60,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'timeAccountMinutes',
        header: t('COMMON.BALANCE'),
        accessorKey: 'timeAccountMinutes',
        size: 60,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value);
        },
        alignment: 'center',
      },
      {
        id: 'projectTimeTotal',
        header: t('COMMON.PROJECTTIME'),
        accessorKey: 'projectTimeTotal',
        footer: () => (
          <Box height="100%">{renderTime(totalValues.projectTimeTotal)}</Box>
        ),
        size: 100,
        cell: (props) => {
          const value = props.getValue();
          return renderTime(value * 60);
        },
        alignment: 'center',
      },
      {
        id: 'projectTimeDifference',
        header: t('COMMON.DIFF_WORKINGTIME'),
        footer: () => (
          <Box height="100%">
            {renderTime(totalValues.projectTimeDifference)}
          </Box>
        ),
        size: 100,
        cell: (props) => {
          if (
            (props.row.original.amountSickness > 0 ||
              props.row.original.amountHoliday > 0 ||
              props.row.original.amountVacation > 0) &&
            !props.row.original.presenceList?.length
          ) {
            return null;
          } else {
            return renderTime(
              props.row.original.projectTimeTotal * 60 -
                props.row.original.isGrossMinutes +
                props.row.original.pauseAmountMinutes
            );
          }
        },
        alignment: 'center',
      },
    ],
    [
      t,
      maxTargetTimeMinutes,
      totalValues.differenceminutes,
      totalValues.isGrossMinutes,
      totalValues.pauseAmountMinutes,
      totalValues.payedTime,
      totalValues.projectTimeDifference,
      totalValues.projectTimeTotal,
      totalValues.targetMinutes,
    ]
  );

  const rowModifiers = useCallback(
    (value: WorkingTimeTimeCardDayInfo) => {
      return {
        isShade1:
          DateTime.fromISO(value.date) <=
          DateTime.fromISO(data.lastInterimBalanceDate),
        isShade2: !!value.amountVacation,
        isShade4: !!value.amountSickness,
        isShade5: !!value.amountHoliday,
        isShade6:
          DateTime.fromISO(value?.date).weekday === 6 ||
          DateTime.fromISO(value?.date).weekday === 7,
      };
    },
    [data.lastInterimBalanceDate]
  );

  const rows = useMemo(() => {
    return data?.timeCardDays || [];
  }, [data?.timeCardDays]);

  const [rowSelection, handleRowSelectionChange] =
    useStatisticTimeCardTableRowSelection<WorkingTimeTimeCardDayInfo>(
      () => table,
      props.onSelectedRowsChange
    );

  const table = useReactTable({
    ...useDefaultReactTableOptions(),
    getSortedRowModel: getSortedRowModel(),
    columns: columns,
    data: rows,
    enableMultiRowSelection: false,
    enableColumnResizing: false,
    entityType: null,
    prepareRowDisplayModifiers: rowModifiers,
    initialState: {
      columnPinning: { left: ['date'] },
    },
    state: {
      rowSelection: rowSelection,
    },
    onRowSelectionChange: handleRowSelectionChange,
  });

  return (
    <VirtualizedTableV8
      table={table}
      fetchMore={() => null}
      classes={tableClasses}
      displayFooter
      allowAutoHeight={true}
    />
  );
};
